import { useContext } from 'react'
import { useTranslation } from 'react-i18next'

import { useTheme } from 'styled-components'

import { Alert, ComboBox, ScrollableTabs, Text, ToastContext } from '@reveni-io/reveni-ui'

import SaveChangesButtons from '@/common/components/SaveChangesButtons/SaveChangesButtons'
import AppContext from '@/common/context/app.context'
import SettingsSection from '@/configuration/components/SettingsSection/SettingsSection'
import { settingsTypes } from '@/configuration/constants'
import { sectionTypes } from '@/configuration/pages/ConfigurationPage/constants'
import { getConfigurationMode } from '@/configuration/pages/ConfigurationPage/helpers'
import useSettingsType from '@/configuration/pages/ConfigurationPage/hooks/useSettingsType/useSettingsType'

import Preview from '../components/Preview/Preview'
import useEmailTemplatesList from '../EmailTemplates/hooks/useEmailTemplatesList/useEmailTemplatesList'
import AppearanceTab from './AppearanceTab/AppearanceTab'
import usePreview from './hooks/usePreview/usePreview'
import { themeTabs } from './hooks/useTabs/constants'
import useTabs from './hooks/useTabs/useTabs'
import useThemesConfiguration from './hooks/useThemesConfiguration/useThemesConfiguration'
import TextStylesTab from './TextStylesTab/TextStylesTab'
import ThemesConfigurationSkeleton from './ThemesConfigurationSkeleton/ThemesConfigurationSkeleton'
import * as Styles from './styles'

const ThemesConfiguration = ({ isUnifiedDashboard }) => {
  const theme = useTheme()
  const { t, i18n } = useTranslation()
  const { info } = useContext(AppContext)
  const { setToastInfo } = useContext(ToastContext)

  const {
    settingsType,
    setSettingsType,
    hasChanges: settingsTypeHasChanges,
    revertSettingsType,
    onSaveSettingsType,
    loadingStoreSettingsType,
  } = useSettingsType({
    returnPortalId: info?.selectedReturnPortal?.id,
    section: sectionTypes.emailTheme,
    isUnifiedDashboard,
    setToastInfo,
  })

  const isDisabledByGlobalSettings = !isUnifiedDashboard && settingsType === settingsTypes.global

  const { tabs, selectedTab, setSelectedTab } = useTabs()

  const {
    colors,
    handleColorChange,
    footerImage,
    handleFooterImageChange,
    footerImageLink,
    handleFooterImageLinkChange,
    textStyles,
    handleFontFamilyChange,
    handleFontSizeChange,
    handleBoldToggle,
    handleItalicToggle,
    handleTextColorChange,
    linkColor,
    handleLinkColorChange,
    handleOnSave,
    savingEmailTheme,
    revertChanges,
    loadingEmailTheme,
    hasChanges,
  } = useThemesConfiguration({
    merchantId: info?.id,
    storeId: info?.selectedStore?.id,
    returnPortalId: info?.selectedReturnPortal?.id,
    onSaveSettingsType,
    settingsTypeHasChanges,
    isUnifiedDashboard,
    setToastInfo,
    t,
    settingsType,
    revertSettingsType,
  })

  const configurationMode = getConfigurationMode({ isUnifiedDashboard, settingsType })

  const { emailTemplates, loading: loadingEmailTemplates } = useEmailTemplatesList({
    configurationMode,
    merchantId: info?.id,
    returnPortalId: info?.selectedReturnPortal?.id,
  })

  const { preview, fetchingPreview, selectedTemplate, handleChangeTemplate } = usePreview({
    colors,
    textStyles,
    linkColor,
    footerImage,
    storeId: info?.selectedStore?.id,
    merchantId: info?.id,
    returnPortalId: info?.selectedReturnPortal?.id,
    templates: emailTemplates,
    setToastInfo,
    isUnifiedDashboard,
    settingsType,
    language: i18n.language,
  })

  if (loadingEmailTheme || loadingStoreSettingsType || loadingEmailTemplates) {
    return <ThemesConfigurationSkeleton />
  }

  return (
    <Styles.Container>
      {!isUnifiedDashboard && info?.allows_unified_dashboard && (
        <SettingsSection type={settingsType} onChange={setSettingsType} />
      )}
      <Text type="bodyMRegular">
        {t(
          'Personaliza los estilos usados para los emails de notificaciones a clientes para que encajen con tu identidad de marca.'
        )}
      </Text>
      <ScrollableTabs tabs={tabs} value={selectedTab} onChange={setSelectedTab} />
      <Alert type="info">
        {t('Estás viendo una vista previa de ejemplo. El contenido real cambiará según el contexto de la transacción.')}
      </Alert>
      <Styles.ColumnsContainer>
        <Styles.LeftColumn>
          {selectedTab === themeTabs.appearance && (
            <AppearanceTab
              colors={colors}
              onColorChange={handleColorChange}
              footerImage={footerImage}
              onFooterImageChange={handleFooterImageChange}
              footerImageLink={footerImageLink}
              onFooterImageLinkChange={handleFooterImageLinkChange}
              disabled={isDisabledByGlobalSettings}
            />
          )}
          {selectedTab === themeTabs.textStyles && (
            <TextStylesTab
              textStyles={textStyles}
              onFontFamilyChange={handleFontFamilyChange}
              onFontSizeChange={handleFontSizeChange}
              onBoldToggle={handleBoldToggle}
              onItalicToggle={handleItalicToggle}
              onTextColorChange={handleTextColorChange}
              linkColor={linkColor}
              onLinkColorChange={handleLinkColorChange}
              disabled={isDisabledByGlobalSettings}
            />
          )}
        </Styles.LeftColumn>
        <Styles.RightColumn>
          <ComboBox
            label={t('Plantilla de vista previa')}
            fullWidth
            menuItems={emailTemplates.map(template => ({ label: template.name, value: template.id }))}
            value={selectedTemplate}
            handleChange={handleChangeTemplate}
            backgroundColor={theme.colors.background.primary}
          />
          <Preview preview={preview?.content} fetchingPreview={fetchingPreview} showAlert={false} iframeHeight="100%" />
        </Styles.RightColumn>
      </Styles.ColumnsContainer>
      <SaveChangesButtons
        hasChanges={settingsTypeHasChanges || hasChanges}
        onSave={handleOnSave}
        onRevertChanges={revertChanges}
        loading={savingEmailTheme}
        disabled={savingEmailTheme}
      />
    </Styles.Container>
  )
}

export default ThemesConfiguration
